import {meService} from "@/src/modules/core/services/me";
import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";

import {safelistingsApi} from "../api";
import type {
  CorrectSafelistingRequest,
  CreateSafelistingRequest,
  ModifyDaysRequest,
  Safelisting,
  UpdateSafelistingRequest,
} from "../types";
import type {ResidentHomeSafelistingsAccountability} from "../types/resident-home-accountability";

class SafelistingsService {
  async getSafelistings(options: ParamsOptions): Promise<GetListResponse<Safelisting>> {
    const {data} = await safelistingsApi.getSafelistings(options);
    return {data: data.data, meta: data.meta};
  }

  async getSafelisting(id: number): Promise<Safelisting> {
    const {data} = await safelistingsApi.getSafelisting(id);
    return data.data;
  }

  async createSafelisting(payload: CreateSafelistingRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.createSafelisting(payload);
    return data.data;
  }

  async updateSafelisting(id: number, payload: UpdateSafelistingRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.updateSafelisting(id, payload);
    return data.data;
  }

  async deleteSafelisting(id: number): Promise<void> {
    await safelistingsApi.deleteSafelisting(id);
  }

  async cancelSafelisting(id: number): Promise<Safelisting> {
    const {data} = await safelistingsApi.cancelSafelisting(id);
    return data.data;
  }

  async correctSafelisting(id: number, payload: CorrectSafelistingRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.correctSafelisting(id, payload);
    return data.data;
  }

  async modifyDays(id: number, payload: ModifyDaysRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.modifyDays(id, payload);
    return data.data;
  }

  async getQrCode(id: number): Promise<unknown> {
    const {data} = await safelistingsApi.getQrCode(id);
    return data.data;
  }

  async getAvailability(options?: ParamsOptions): Promise<unknown> {
    const {data} = await safelistingsApi.getAvailability(options);
    return data.data;
  }

  async getAccountability(options?: ParamsOptions): Promise<unknown> {
    const {data} = await safelistingsApi.getAccountability(options);
    return data.data;
  }

  /** Resident home quota — no vehicle_id/plate; token scopes household/property. */
  async getResidentHomeAccountability(): Promise<ResidentHomeSafelistingsAccountability> {
    const {data} = await safelistingsApi.getAccountability();
    return data.data as ResidentHomeSafelistingsAccountability;
  }

  async getHistory(options?: ParamsOptions): Promise<unknown> {
    const {data} = await safelistingsApi.getHistory(options);
    return data.data;
  }

  getIsCreateEnabled(): boolean {
    return meService.getHasPermission("safelisting store");
  }

  getIsUpdateEnabled(): boolean {
    return meService.getHasPermission("safelisting update");
  }

  getIsDeleteEnabled(): boolean {
    return meService.getHasPermission("safelisting delete");
  }

  getIsCancelEnabled(): boolean {
    return meService.getHasPermission("safelisting cancel");
  }

  /** Optional policy action; aligns with Laravel `safelisting / show`. */
  getIsShowEnabled(): boolean {
    return meService.getHasPermission("safelisting show");
  }

  getIsCorrectEnabled(): boolean {
    return meService.getHasPermission("safelisting correct");
  }
}

export const safelistingsService = new SafelistingsService();
